/**
 * Formulare / generischer Formular-Builder (CompanyBrain Welle 3d, BEN-107).
 * Hooks fuer Liste/Detail/Anlegen/Bearbeiten/Status/Loeschen, Vorlagen, Antworten (ausfuellen,
 * listen) und CSV-Export. Persistenz Orchestrator (Postgres, tenant+user-scoped).
 * Rollenbewusst: Kunde fuellt aus, Berater wertet aus.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi, buildTenantScopedHeaders } from "./api";
import { useAuth } from "./auth";
import { useTenant } from "./tenant";

export type FieldType = "text" | "textarea" | "number" | "select" | "date" | "checkbox";

export type FormField = {
  id: string;
  label: string;
  type: FieldType;
  required: boolean;
  options: string[];
  help: string;
};

export type FormDef = {
  id: string;
  user_sub?: string;
  title: string;
  description: string;
  context_type: string;
  context_ref: string;
  fields: FormField[];
  status: string;
  assigned_role: string;
  meta: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
};

export type FormSummary = {
  id: string;
  title: string;
  description: string;
  context_type: string;
  context_ref: string;
  status: string;
  assigned_role: string;
  field_count: number;
  response_count: number;
  assignment_count: number;
  pending_assignment_count: number;
  created_at: string | null;
  updated_at: string | null;
};

export type FormResponse = {
  id: string;
  form_id: string;
  user_sub?: string;
  answers: Record<string, unknown>;
  submitted_by: string;
  submitted_role: string;
  meta: Record<string, unknown>;
  created_at: string | null;
};

export type FormsStats = { total: number; aktiv: number; pending_assigned: number; responses: number };
export type FormTemplate = {
  key: string;
  name: string;
  description: string;
  context_type: string;
  grounded?: boolean;
  source_code?: string;
  source?: string;
  katalog_version?: string | null;
};

export type FormAssignment = {
  id: string;
  user_sub: string;
  form_id: string;
  assignee_sub: string;
  assignee_role: "" | "kunde" | "berater" | "alle";
  assignee_label: string;
  due_date: string;
  status: "offen" | "erledigt" | "storniert";
  completed_by?: string;
  created_at: string | null;
  completed_at?: string | null;
};

export type FormAggregateField = {
  id: string;
  label: string;
  type: FieldType;
  answered: number;
  missing: number;
  count?: number;
  mean?: number | null;
  min?: number | null;
  max?: number | null;
  counts?: Record<string, number>;
};

export type FormAggregation = {
  form_id: string;
  response_count: number;
  fields: FormAggregateField[];
  privacy: string;
};

export type FormInput = {
  title?: string;
  description?: string;
  context_type?: string;
  context_ref?: string;
  fields?: Partial<FormField>[];
  status?: string;
  assigned_role?: string;
  meta?: Record<string, unknown>;
};

export type FormsListResponse = {
  tenant_id: string;
  count: number;
  items: FormSummary[];
  persistent: boolean;
  stats: FormsStats;
};

export const FIELD_TYPE_LABEL: Record<FieldType, string> = {
  text: "Text (kurz)",
  textarea: "Text (lang)",
  number: "Zahl",
  select: "Auswahl",
  date: "Datum",
  checkbox: "Checkbox (ja/nein)",
};

export const CONTEXT_TYPE_LABEL: Record<string, string> = {
  prozessschritt: "Prozessschritt",
  ressort: "Ressort",
  usecase: "Use-Case",
  frei: "Frei",
};

export const FORM_STATUS_LABEL: Record<string, string> = {
  entwurf: "Entwurf",
  aktiv: "Aktiv",
  archiviert: "Archiviert",
};

export function useFormsList(status: string = "all", q: string = "") {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["forms-list", effectiveTenantId, status, q],
    queryFn: () => api.get<FormsListResponse>(`/forms?status=${encodeURIComponent(status)}&q=${encodeURIComponent(q)}&limit=300`),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 15_000,
  });
}

export function useFormsStats() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["forms-stats", effectiveTenantId],
    queryFn: () => api.get<FormsStats & { tenant_id: string }>("/forms/stats"),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 30_000,
  });
}

export function useFormTemplates() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["forms-templates", effectiveTenantId],
    queryFn: () => api.get<{ templates: FormTemplate[] }>("/forms/templates"),
    enabled: Boolean(effectiveTenantId),
    staleTime: 5 * 60_000,
  });
}

export function useFormDetail(id: string | null) {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["forms-detail", effectiveTenantId, id],
    queryFn: () => api.get<{ form: FormDef }>(`/forms/${encodeURIComponent(id!)}`),
    enabled: Boolean(effectiveTenantId) && Boolean(id),
    retry: 1,
  });
}

export function useFormResponses(id: string | null) {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["forms-responses", effectiveTenantId, id],
    queryFn: () => api.get<{ form_id: string; count: number; items: FormResponse[]; fields: FormField[] }>(
      `/forms/${encodeURIComponent(id!)}/responses`),
    enabled: Boolean(effectiveTenantId) && Boolean(id),
    retry: 1,
  });
}

export function useFormAggregation(id: string | null) {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["forms-aggregation", effectiveTenantId, id],
    queryFn: () => api.get<FormAggregation>(`/forms/${encodeURIComponent(id!)}/aggregation`),
    enabled: Boolean(effectiveTenantId) && Boolean(id),
    retry: 1,
  });
}

export function useFormAssignments(id: string | null, status: string = "all") {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["forms-assignments", effectiveTenantId, id, status],
    queryFn: () => api.get<{ form_id: string; count: number; items: FormAssignment[] }>(
      `/forms/${encodeURIComponent(id!)}/assignments?status=${encodeURIComponent(status)}`),
    enabled: Boolean(effectiveTenantId) && Boolean(id),
    retry: 1,
  });
}

function useInvalidateForms() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["forms-list"] });
    qc.invalidateQueries({ queryKey: ["forms-stats"] });
    qc.invalidateQueries({ queryKey: ["forms-assignments"] });
    qc.invalidateQueries({ queryKey: ["forms-aggregation"] });
    qc.invalidateQueries({ queryKey: ["next-actions"] });
  };
}

export function useCreateForm() {
  const api = useApi();
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: (input: FormInput) => api.post<{ ok: boolean; form: FormDef }>("/forms", input),
    onSuccess: invalidate,
  });
}

export function useFormFromTemplate() {
  const api = useApi();
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: (input: { template: string; title?: string; context_type?: string; context_ref?: string }) =>
      api.post<{ ok: boolean; form: FormDef }>("/forms/from-template", input),
    onSuccess: invalidate,
  });
}

export function useUpdateForm() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: FormInput }) =>
      api.put<{ ok: boolean; form: FormDef }>(`/forms/${encodeURIComponent(id)}`, input),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["forms-detail"] });
    },
  });
}

export function useSetFormStatus() {
  const api = useApi();
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.post<{ ok: boolean; form: FormDef }>(`/forms/${encodeURIComponent(id)}/status`, { status }),
    onSuccess: invalidate,
  });
}

export function useDeleteForm() {
  const api = useApi();
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean; id: string }>(`/forms/${encodeURIComponent(id)}`),
    onSuccess: invalidate,
  });
}

export function useSubmitResponse() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: ({ id, answers, submitted_role, assignment_id }: {
      id: string;
      answers: Record<string, unknown>;
      submitted_role?: string;
      assignment_id?: string;
    }) =>
      api.post<{ ok: boolean; response: FormResponse; persistent: boolean }>(
        `/forms/${encodeURIComponent(id)}/responses`, { answers, submitted_role, assignment_id }),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["forms-responses"] });
      qc.invalidateQueries({ queryKey: ["forms-aggregation"] });
      qc.invalidateQueries({ queryKey: ["forms-assignments"] });
    },
  });
}

export function useDeleteResponse() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rid }: { id: string; rid: string }) =>
      api.del<{ ok: boolean; id: string }>(`/forms/${encodeURIComponent(id)}/responses/${encodeURIComponent(rid)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["forms-responses"] });
      qc.invalidateQueries({ queryKey: ["forms-list"] });
      qc.invalidateQueries({ queryKey: ["forms-aggregation"] });
    },
  });
}

export function useCreateAssignment() {
  const api = useApi();
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: ({ id, assignee_role, assignee_label, due_date }: {
      id: string;
      assignee_role: "kunde" | "berater" | "alle";
      assignee_label?: string;
      due_date?: string;
    }) => api.post<{ ok: boolean; assignment: FormAssignment }>(
      `/forms/${encodeURIComponent(id)}/assignments`, { assignee_role, assignee_label, due_date }),
    onSuccess: invalidate,
  });
}

export function useCancelAssignment() {
  const api = useApi();
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: ({ id, assignmentId }: { id: string; assignmentId: string }) =>
      api.del<{ ok: boolean; id: string }>(
        `/forms/${encodeURIComponent(id)}/assignments/${encodeURIComponent(assignmentId)}`),
    onSuccess: invalidate,
  });
}

/** CSV-Download der Antworten (nutzt die tenant-scoped Auth-Header wie der Rest der App). */
export function useExportCsv() {
  const { token } = useAuth();
  const { effectiveTenantId } = useTenant();
  return async (formId: string, filename: string) => {
    const headers = buildTenantScopedHeaders({ token, tenantId: effectiveTenantId });
    const base = (import.meta.env.VITE_API_BASE ?? "/v1") as string;
    const res = await fetch(`${base}/forms/${encodeURIComponent(formId)}/export`, { headers });
    if (!res.ok) throw new Error(`Export fehlgeschlagen (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename || "formular"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
}
